import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
 
import { messaging } from "../firebase";
import { useChatStore } from "../store/useChatStore";
import { onMessage } from "firebase/messaging";
import { checkFCMToken } from "../services/api";
import { API_URLS } from "../constants/apiUrls";
import {
  NOTIFICATION_STORAGE_KEYS,
  PERMISSION_STATE,
  FCM_BANNER_TEXT,
} from "../constants/Notification";
const FCM_API_KEY = process.env.REACT_APP_FIREBASE_API_KEY;
import { useAuth } from "../context/AuthContext";
import "../styles/FCMNotificationBanner.css"; // Import the CSS file
import { browserName } from 'react-device-detect'; 
import {
  isFcmSupported,
  getFcmTokenFromServiceWorker,
  registerFcmToken,
  unregisterFcmToken,
  unregisterFirebaseServiceWorkers,
  getStoredFcmToken,
  clearStoredFcmToken,
} from "../utils/fcmOperations";

const FCMNotificationBanner = () => {
   
    const isSupported = isFcmSupported();

    const permission = useChatStore((s) => s.permission);
    const setPermission = useChatStore((s) => s.setPermission);

    const loading = useChatStore((s) => s.fcmloading);
    const setLoading = useChatStore((s) => s.setFcmLoading);

    const closed = useChatStore((s) => s.closed);
    const setClosed = useChatStore((s) => s.setClosed);

    const chatClient = useChatStore((s) => s.chatClient); 
    const { authData } = useAuth();
    const authToken = authData.systemToken;
    const [isTokenValid, setIsTokenValid] = useState(false); // State to track token validity
    
    // Check if user has ever enabled notifications (persisted in localStorage)
    const [hasEverEnabled, setHasEverEnabled] = useState(() => {
        return localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_BANNER_DISMISSED) === "true";
    });
    
    const clearTokenAndResetPermissions = () => {
        clearStoredFcmToken();
        setPermission(PERMISSION_STATE.DEFAULT);
        console.log("Invalid FCM token. Cleared token and reset permissions.");
    };
    useEffect(() => {
        const validateToken = async () => {
            const storedToken = getStoredFcmToken();
            if (storedToken) {
                try {
                    setLoading(true);
                    const tokenExists = await checkFCMToken(API_URLS.FCM_GET, storedToken, authToken, browserName);
                    if (!tokenExists) {
                        clearTokenAndResetPermissions(); // Use the common method
                    }
                    setIsTokenValid(!!tokenExists); // Set token validity based on response
                  
                } catch (error) {
                    console.error("Error checking FCM token:", error);
                    setIsTokenValid(false);
                } finally {
                    setLoading(false);
                }
            } else {
                clearTokenAndResetPermissions(); // Use the common method
                setIsTokenValid(false);                
            }

        };

        validateToken();
    }, [authToken]);

    // Determine if banner should be visible (same logic as render condition)
    const storedToken = getStoredFcmToken();
    const shouldShowBanner = isSupported && 
        permission !== PERMISSION_STATE.GRANTED && 
        !closed && 
        !isTokenValid && 
        !storedToken && 
        !hasEverEnabled;

    const bannerRef = useRef(null);

    useLayoutEffect(() => {
        // Measure actual banner height and set CSS variable
        const updateBannerHeight = () => {
            if (shouldShowBanner && bannerRef.current) {
                const height = bannerRef.current.getBoundingClientRect().height;
                document.documentElement.style.setProperty(
                    "--fcm-banner-height", 
                    `${height}px`
                );
            } else {
                document.documentElement.style.setProperty(
                    "--fcm-banner-height", 
                    "0px"
                );
            }
        };

        updateBannerHeight();
        window.addEventListener('resize', updateBannerHeight);
        return () => window.removeEventListener('resize', updateBannerHeight);
    }, [shouldShowBanner]);

    useEffect(() => {
        // Remove automatic registration of FCM token
        const handleFocus = () => {
            const storedToken = getStoredFcmToken();

            if (isSupported && permission === PERMISSION_STATE.GRANTED) {
                if (!storedToken) {
                    console.log("FCM token not available. Resetting permissions and showing banner.");
                    clearTokenAndResetPermissions(); // Use the common method
                } else {
                    console.log("Notification permission already granted.");
                }
            } else {
                console.log("Notification permission not granted.");
            }
        };
        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [permission]);

    useEffect(() => {
        const unsubscribe = onMessage(messaging, (payload) => {
            if (Notification.permission === PERMISSION_STATE.GRANTED) {
                const title = payload.notification?.title || payload.data?.title || "Notification";
                const options = {
                    body: payload.notification?.body || payload.data?.body || "",
                    icon: payload.notification?.icon || payload.data?.icon || "/icon-192.png",
                    data: payload.data || {},
                };
                const notification = new Notification(title, options);
                notification.onclick = () => {
                    window.focus();
                };
            }
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        const handleBeforeUnload = async () => {
            // Check if the user is navigating away (tab/browser close) and not refreshing
            if (performance.getEntriesByType("navigation")[0]?.type === "reload") {
                console.log("Skipping API call during page reload.");
                return;
            }
            await deregisterFCMTokenInternal();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    const registerFCMTokenInternal = async () => {       
        try {
            if (isSupported && (!chatClient || !chatClient.user)) {
                await deregisterFCMTokenInternal();
                setPermission(PERMISSION_STATE.DENIED);
                return;
            }
            if ("serviceWorker" in navigator && FCM_API_KEY && isSupported) {
                const fcmToken = await getFcmTokenFromServiceWorker();
                const storedToken = getStoredFcmToken();
                if (fcmToken && chatClient && chatClient.user && storedToken !== fcmToken) {
                    await registerFcmToken(authToken, fcmToken);
                }
            } else {
                console.warn("Service Worker or FCM API key is not supported or available.");
            }

        } catch (err) {
            console.error("Failed to register FCM token", err);
            setPermission(PERMISSION_STATE.DENIED);
            await deregisterFCMTokenInternal();

        }
    };

    const deregisterFCMTokenInternal = async () => {
        try {
            await unregisterFcmToken(authToken);
            await unregisterFirebaseServiceWorkers();
        } catch (error) {
            setPermission(PERMISSION_STATE.DENIED);
            console.error("Failed to de-register FCM token", error);
        }
    };

    const handleAllowNotifications = async () => {
        setLoading(true);
        try {
            const result = await Notification.requestPermission();
            if (result === PERMISSION_STATE.GRANTED) {
                // Don't hide banner yet - wait for FCM registration to complete
                await registerFCMTokenInternal();
                
                // Browser permission granted - hide banner and show bell
                // Bell will show faBell (Pause) if FCM_TOKEN exists, faBellSlash (Resume) if not
                setPermission(PERMISSION_STATE.GRANTED);
                localStorage.setItem(NOTIFICATION_STORAGE_KEYS.FCM_BANNER_DISMISSED, "true");
                setHasEverEnabled(true);
            } else {
                setPermission(PERMISSION_STATE.DENIED);
            }
        } catch {
            // If browser permission was granted but FCM registration failed
            if (Notification.permission === PERMISSION_STATE.GRANTED) {
                setPermission(PERMISSION_STATE.GRANTED);
                localStorage.setItem(NOTIFICATION_STORAGE_KEYS.FCM_BANNER_DISMISSED, "true");
                setHasEverEnabled(true);
            } else {
                setPermission(PERMISSION_STATE.DENIED);
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle banner close (X button) - persist the dismissed state
    const handleCloseBanner = () => {
        setClosed(true);
    };

    // Don't show banner if conditions not met
    if (!shouldShowBanner) return null;

    return (
        <>
            {loading && (
                <div className="loading-bar fcm-loading-bar">
                    <div className="loading-bar-inner fcm-loading-bar-inner" />
                </div>
            )}
            <div ref={bannerRef} className="fcm-notification-banner">
                <div className="fcm-banner-content">
                    <span className="fcm-banner-text">
                        {FCM_BANNER_TEXT.PERMISSION_MESSAGE}
                    </span>
                    <button
                        className={`fcm-enable-button ${loading ? "disabled" : ""}`}
                        onClick={handleAllowNotifications}
                        disabled={loading}
                        aria-label="Enable notifications"
                        >
                            <svg
                                className="fcm-check-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                width="14"
                                height="14"
                                fill="currentColor"
                            >
                                <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z" />
                            </svg>
                            <span>{FCM_BANNER_TEXT.ENABLE_BUTTON}</span>
                        </button>
                    </div>
                    <button
                        className="fcm-close-button"
                        onClick={handleCloseBanner}
                        aria-label="Close"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
        </>
    );
};

export default FCMNotificationBanner;